import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  IonContent, IonPage, IonButton, IonHeader, IonIcon, IonRouterLink,
} from '@ionic/react';
import {
  Box, Stack, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import useFetch, { CachePolicies } from 'use-http';
import { useHistory } from 'react-router-dom';
import { useAppState } from '../../providers/app-state/app-state-provider';
import { catchupSelector } from '../../redux/slices/catchup';
import { useAuthContext } from '../../providers/auth-provider';
import CatchupCard from './CatchupCard';
import ProgressDots from '../../common/atoms/progressDots/ProgressDots';
import { computePaginationState } from './compute_catchup_container';
import './Catchup.css';
import appStrings from '../../common/app_strings';
import appColors from '../../common/app_colors';
import BaseModal from '../../common/atoms/BaseModal/BaseModal';

const CATCHUP_STRINGS = appStrings.catchup;
const { container, text, buttons } = appColors;

const styles = {
  endStateTextStyle: {
    textAlign: 'center',
    fontSize: '32px',
    fontWeight: 700,
    whiteSpace: 'pre-line',
    paddingTop: '20px',
    lineHeight: '1.25',
  },
  backIconContainerStyles: {
    width: '25px',
    height: '25px',
    position: 'fixed',
    cursor: 'pointer',
    left: '20px',
    zIndex: 999,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  headerStyles: {
    width: '100%',
    padding: '60px 0 10px',
    marginBottom: 'auto 0',
    position: 'fixed',
    top: 0,
    backgroundColor: container.splashBackground,
    zIndex: 998,
  },
  headerStylesWeb: {
    padding: '30px 0 20px',
  },
  curveSvgStyles: {
    position: 'absolute',
    zIndex: 1,
    top: '-1rem',
  },
  dotSvgStyles: {
    position: 'absolute',
    left: '40%',
    top: '55%',
    zIndex: 1,
  },
  catchupHeaderButtonStyles: {
    '--padding-start': '2px',
    '--padding-end': '7px',
    width: '100%',
    height: '32px',
    borderRadius: '8px',
    border: `2px solid ${text.darkNavy}`,
    textTransform: 'none',
  },
  catchupHeaderButtonStylesLoading: {
    backgroundColor: container.electricBlue,
  },
  headerButtonIconStyles: {
    fontSize: '2.125rem',
    marginLeft: '-5px',
  },
  catchupHeaderTextStyles: {
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: 'normal',
    marginLeft: '-3px',
  },
  fadeInAnimation: {
    animation: 'fadeIn 0.5s ease-in',
    opacity: 1,
  },
  backIconTextStyle: {
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: 'normal',
    color: text.whiteText,
  },
  modalContainerStyles: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  whyAmISeeingTextStyle: {
    color: text.whiteText,
    fontSize: '12px',
    textAlign: 'center',
    textDecoration: 'underline',
    cursor: 'pointer',
    marginTop: '10px',
  },
  whyAmISeeingModalHeader: {
    color: text.darkNavy,
    fontSize: '22px',
    fontWeight: 700,
  },
  whyAmISeeingModalBody: {
    fontSize: '14px',
    color: text.lightBlack,
  },
};

const BATCH_SIZE = 10; // Number of interactions to collect before sending

const CurveSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="152" height="95" viewBox="0 0 152 95" fill="none">
    <circle cx="49.5" cy="-7.5" r="100" stroke="#03295F" strokeWidth="5" />
  </svg>
);

const DotSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
    <circle cx="18" cy="18" r="18" fill="#03295F" />
  </svg>
);

const Catchup = ({ isHeaderItem = false }) => {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEndState, setShowEndState] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [interactions, setInteractions] = useState([]);
  const [paginationMeta, setPaginationMeta] = useState({
    current: 0,
    total: posts.length,
  });
  const [isResponding, setIsResponding] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { unreadCount: unreadCatchupCount } = useSelector(catchupSelector);
  const { get, post, put } = useFetch({ cachePolicy: CachePolicies.NO_CACHE });
  const { user } = useAuthContext();
  const { platform } = useAppState();
  const history = useHistory();
  const currentPost = posts[paginationMeta.current];
  const [swipeDirection, setSwipeDirection] = useState(null);

  const { paginate, paginationTotal, paginationCurrent } = computePaginationState({
    posts,
    paginationMeta,
  });

  const showProgressDots = paginate && currentPost && posts.length > 0;
  const showCatchupCards = currentPost && !showEndState && posts.length > 0;
  const catchupLargerThan99 = unreadCatchupCount > 99;

  const nextAsk = () => {
    let { current } = paginationMeta;
    current++;

    if (current >= posts.length) {
      setShowEndState(true);
      return;
    }

    setPaginationMeta({
      ...paginationMeta,
      current,
    });
  };

  const fetchCatchupPosts = async () => {
    try {
      const params = new URLSearchParams();
      params.append('sort', '["created_at","DESC"]');
      params.append('catchup_feed', 'true');

      const res = await get(`/user-feed?${params.toString()}`);

      if (!res || res.error) {
        toast(CATCHUP_STRINGS.feedUnavailable);
        console.error('Error fetching posts: ', res.error || 'Unknown error');
        return [];
      }

      return res;
    } catch (e) {
      toast(CATCHUP_STRINGS.error);
      console.error('Error fetching posts: ', e);
      return [];
    }
  };

  const saveInteractions = async () => {
    if (interactions.length === 0) return;

    try {
      await post('/user-post-interactions', {
        interactions,
        context: 'catch_up',
      });
      // Clear processed interactions
      setInteractions([]);
    } catch (e) {
      toast(CATCHUP_STRINGS.feedUnavailable);
      console.error('Error saving interactions: ', e);
    }
  };

  const addInteraction = async (postId, interactionType) => {
    const newInteraction = {
      post_id: postId,
      interaction_type: interactionType,
    };

    setInteractions((prev) => [...prev, newInteraction]);

    // Process batch if we've reached BATCH_SIZE
    if (interactions.length + 1 >= BATCH_SIZE) {
      await saveInteractions();
    }
  };

  const handleAnimationEnd = () => {
    if (swipeDirection === 'left' || swipeDirection === 'right') {
      nextAsk();
    }
    setSwipeDirection(null);
  };

  const handlePass = async () => {
    setSwipeDirection('left');
    await addInteraction(currentPost.id, 'ignored');
  };

  const handleLater = async () => {
    setSwipeDirection('right');
    await addInteraction(currentPost.id, 'read');
  };

  // eslint-disable-next-line consistent-return
  const handleResponse = async (response) => {
    try {
      const commentPayload = {
        body: response.response,
        post_id: response.postId,
        author_id: user.id,
        attachments: response.media?.id ? [response.media?.id] : [],
      };

      const res = (response?.commentId)
        ? await put(`/comments/${response.commentId}`, commentPayload)
        : await post('/comments', commentPayload);

      // only want to add the interaction if we don't already have an interaction for this post
      if (!interactions.some((interaction) => interaction.post_id === response.postId)) {
        await addInteraction(response.postId, 'responded');
      }
      setShowCelebration(true);
      return res;
    } catch (e) {
      toast(CATCHUP_STRINGS.feedUnavailable);
      console.error('Error creating response: ', e);
    }
  };

  const handleNextUp = () => {
    setShowCelebration(false);
    setSwipeDirection('fade');
    nextAsk();
  };

  const handleCatchupClick = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const fetchedPosts = await fetchCatchupPosts();
    setIsLoading(false);

    if (fetchedPosts.length > 0) {
      history.push('/catchup', {
        posts: fetchedPosts,
        animate: true,
      });
    } else {
      history.push('/catchup');
    }
  };

  const handleBackClick = async () => {
    if (interactions.length > 0) {
      await saveInteractions();
    }
    history.replace('/tabs/feed');
  };

  useEffect(() => {
    const initializePosts = async () => {
      const locationState = history.location.state;
      if (locationState?.posts) {
        setPosts(locationState.posts);
      } else {
        const fetchedPosts = await fetchCatchupPosts();
        setPosts(fetchedPosts);
        if (fetchedPosts.length === 0) {
          setShowEndState(true);
        }
      }
    };

    if (!isHeaderItem) {
      initializePosts();
    }
  }, [isHeaderItem]);

  useEffect(() => {
    if (showEndState && interactions.length > 0) {
      saveInteractions();
    }
  }, [showEndState]);

  if (isHeaderItem) {
    return (
      <IonRouterLink onClick={(e) => handleCatchupClick(e)} style={{ marginRight: '10px' }}>
        <IonButton
          style={{
            ...styles.catchupHeaderButtonStyles,
            ...(isLoading && styles.catchupHeaderButtonStylesLoading),
          }}
        >
          <IonIcon
            slot="icon-only"
            icon="/assets/icon/catchup/bottle.svg"
            style={styles.headerButtonIconStyles}
          />
          <Typography sx={styles.catchupHeaderTextStyles}>
            {CATCHUP_STRINGS.catchup}
          </Typography>
        </IonButton>
        {!!unreadCatchupCount && (
          <div
            className="catchup-unread-dot notification-dot"
            style={{
              fontSize: catchupLargerThan99 ? '8px' : '10px',
            }}
          >
            {unreadCatchupCount > 500 ? '500+' : unreadCatchupCount}
          </div>
        )}
      </IonRouterLink>
    );
  }

  return (
    <IonPage
      className="catchup-container"
      style={{
        '--background': container.splashBackground,
        ...styles.fadeInAnimation,
      }}
    >
      <IonHeader className="ion-no-border" style={{ ...styles.headerStyles, ...(!platform?.isNativePlatform && styles.headerStylesWeb) }}>
        <Box>
          <Box as="back-icon" style={styles.backIconContainerStyles} onClick={handleBackClick}>
            <img src="assets/back-arrow-white.svg" alt="back-button" />
            <Typography sx={styles.backIconTextStyle}>{appStrings.common.explore}</Typography>
          </Box>
          {
            showProgressDots && (
              <ProgressDots
                totalNumber={paginationTotal}
                activeNumber={paginationCurrent}
                maxVisibleDots={4}
                styleOverride={{
                  backgroundColor: container.white,
                  zIndex: 10,
                }}
              />
            )
          }
        </Box>
        <Box sx={styles.curveSvgStyles}>
          <CurveSvg />
        </Box>
        <Box sx={styles.dotSvgStyles}>
          <DotSvg />
        </Box>
      </IonHeader>
      <IonContent
        fullscreen
        style={{
          '--background': container.splashBackground,
          '--padding-top': '7rem',
        }}
      >
        <Box
          className="catchup-content"
        >
          {showCatchupCards ? (
            <>
              <Box
                className={`card-container ${swipeDirection ? `swipe-${swipeDirection}` : ''}`}
                onAnimationEnd={handleAnimationEnd}
              >
                <CatchupCard
                  post={currentPost}
                  onResponse={handleResponse}
                  user={user}
                  showCelebration={showCelebration}
                  setShowCelebration={setShowCelebration}
                  isResponding={isResponding}
                  setIsResponding={setIsResponding}
                />
              </Box>

              <Stack
                direction="row"
                spacing={2}
                className="navigation-container"
              >
                {showCelebration ? (
                  <IonButton
                    className="catchup-button"
                    onClick={handleNextUp}
                    style={{
                      margin: 0,
                      '--background': buttons.tetriaryBackground,
                      '--color': text.white,
                    }}
                  >
                    {CATCHUP_STRINGS.nextUp}
                  </IonButton>
                ) : (
                  <>
                    <IonButton
                      className="catchup-button"
                      onClick={handlePass}
                      style={{
                        margin: 0,
                        '--background': buttons.tetriaryBackground,
                        '--color': text.white,
                      }}
                    >
                      {CATCHUP_STRINGS.pass}
                    </IonButton>
                    <IonButton
                      className="catchup-button"
                      onClick={handleLater}
                      style={{
                        margin: 0,
                        '--background': container.beige,
                        '--color': text.darkNavy,
                      }}
                    >
                      {CATCHUP_STRINGS.later}
                    </IonButton>
                  </>
                )}
              </Stack>

              <Typography onClick={() => setShowModal(true)} sx={styles.whyAmISeeingTextStyle}>
                {CATCHUP_STRINGS.whyAmISeeing}
              </Typography>

              <BaseModal
                isOpen={showModal}
                handleClose={() => setShowModal(false)}
              >
                <Stack spacing={2} sx={styles.modalContainerStyles}>
                  <Typography sx={styles.whyAmISeeingModalHeader}>
                    {CATCHUP_STRINGS.whyAmISeeing}
                  </Typography>
                  <Typography sx={styles.whyAmISeeingModalBody}>
                    {CATCHUP_STRINGS.whyYouAreSeeing}
                  </Typography>
                </Stack>
              </BaseModal>
            </>
          ) : showEndState && (
            <>
              <Box className="end-state-container">
                <Stack alignItems="center" sx={{ marginBottom: '4rem', ...(platform?.isNativePlatform && { marginTop: '4rem' }) }}>
                  <img
                    src="/assets/icon/catchup/blue_high_five.svg"
                    alt="All caught up"
                    className="end-state-image"
                  />
                  <Typography variant="h6" sx={styles.endStateTextStyle}>
                    {CATCHUP_STRINGS.allCaughtUp}
                  </Typography>
                </Stack>
              </Box>
              <Stack
                direction="row"
                spacing={2}
                className="navigation-container"
                justifyContent="center"
              >
                <IonButton
                  className="cta-button"
                  onClick={() => history.replace('/tabs/feed')}
                >
                  {CATCHUP_STRINGS.done}
                </IonButton>
              </Stack>
            </>
          )}
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default Catchup;
